// src/components/forms/fieldsConfig/produitFields.js
import { getAPI } from '../../../apis/backend/apiNode';
import { useState, useCallback } from 'react';

// Caches séparés pour éviter les conflits
const caches = {
  categories: { data: null, timestamp: null },
  suppliers: { data: null, timestamp: null },
  fields: { data: null, timestamp: null }
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Fonction utilitaire pour gérer le cache
const getCachedData = (cacheKey, forceRefresh = false) => {
  const cache = caches[cacheKey];
  const now = Date.now();
  
  if (!forceRefresh && cache.data && cache.timestamp && (now - cache.timestamp) < CACHE_DURATION) {
   
    return cache.data;
  }
  return null;
};

const setCachedData = (cacheKey, data) => {
  caches[cacheKey] = {
    data,
    timestamp: Date.now()
  };
};

// Fonction pour charger les catégories avec cache
export const loadCategories = async (forceRefresh = false) => {
  const cached = getCachedData('categories', forceRefresh);
  if (cached) return cached;
  
  try {
    const response = await getAPI('stock', '/categories?limit=100&active=true');
    if (response?.success) {
      const categoriesList = response.data?.categories || response.data || [];
      const formattedCategories = categoriesList.map(cat => ({
        value: cat._id,
        label: cat.name,
        code: cat.code,
        description: cat.description
      }));
      setCachedData('categories', formattedCategories);
      return formattedCategories;
    }
    return [];
  } catch (error) {
    console.error('Erreur chargement catégories:', error);
    return [];
  }
};

// Fonction pour charger les fournisseurs avec cache
export const loadSuppliers = async (forceRefresh = false) => {
  const cached = getCachedData('suppliers', forceRefresh);
  if (cached) return cached;
  
  try {
    const response = await getAPI('achat', '/fournisseurs?limit=100&active=true');
    if (response?.success) {
      const suppliersList = response.data?.fournisseurs || response.data || [];
      const formattedSuppliers = suppliersList.map(sup => ({
        value: sup._id,
        label: sup.nom || sup.name || 'Sans nom',
        email: sup.email,
        telephone: sup.telephone,
        ville: sup.ville
      }));
      setCachedData('suppliers', formattedSuppliers);
      return formattedSuppliers;
    }
    return [];
  } catch (error) {
    console.error('Erreur chargement fournisseurs:', error);
    return [];
  }
};

const getBasicProduitFields = () => {
  return [
    { 
      name: 'name', 
      label: 'Nom du produit', 
      type: 'text', 
      required: true,
      placeholder: 'Nom du produit',
      col: 12
    },
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea', 
      rows: 3,
      placeholder: 'Description détaillée du produit...',
      col: 12
    },

    { 
      name: 'unit', 
      label: 'Unité de mesure', 
      type: 'select', 
      required: true,
      options: [
        { value: 'Piece', label: 'Pièce' },
        { value: 'Kg', label: 'Kilogramme' },
        { value: 'Meter', label: 'Mètre' },
        { value: 'Litre', label: 'Litre' },
        { value: 'ML', label: 'Millilitre' },
        { value: 'Day', label: 'Jour' }
      ],
      defaultValue: 'Piece',
      col: 6
    }
  ];
};

// Fonction pour obtenir les champs avec options dynamiques - version avec cache
export const getProduitCreationFields = async (forceRefresh = false) => {
  const cached = getCachedData('fields', forceRefresh);
  if (cached) return cached;
  
  try {
    // Charger les options en parallèle avec gestion d'erreur individuelle
    const [categoriesOptions, suppliersOptions] = await Promise.allSettled([
      loadCategories(forceRefresh),
      loadSuppliers(forceRefresh)
    ]);

    const categories = categoriesOptions.status === 'fulfilled' ? categoriesOptions.value : [];
    const suppliers = suppliersOptions.status === 'fulfilled' ? suppliersOptions.value : [];

    // Construire les champs
    const loadedFields = [
      ...getBasicProduitFields(),
      { 
        name: 'categoryId', 
        label: 'Catégorie', 
        type: 'select', 
        options: categories,
        placeholder: categories.length > 0 ? 'Sélectionner une catégorie' : 'Aucune catégorie disponible',
        disabled: categories.length === 0,
        helpText: categories.length === 0 ? 'Aucune catégorie active trouvée' : '',
        col: 6
      },
      { 
        name: 'supplierId', 
        label: 'Fournisseur principal', 
        type: 'select', 
        options: suppliers,
        placeholder: suppliers.length > 0 ? 'Sélectionner un fournisseur' : 'Aucun fournisseur disponible',
        disabled: suppliers.length === 0,
        helpText: suppliers.length === 0 ? 'Aucun fournisseur actif trouvé' : '',
        col: 6
      },
      { 
        name: 'costPrice', 
        label: 'Prix d\'achat (HT)', 
        type: 'number', 
        min: 0,
        step: 0.01,
        placeholder: '0.00',
        helpText: 'Prix d\'achat chez le fournisseur',
        col: 6
      },
      { 
        name: 'sellingPrice', 
        label: 'Prix de vente (HT)', 
        type: 'number', 
        min: 0,
        step: 0.01,
        placeholder: '0.00',
        helpText: 'Prix de vente conseillé',
        col: 6
      }
    ];

    setCachedData('fields', loadedFields);
    return loadedFields;
  } catch (error) {
    console.error('Erreur chargement champs produit:', error);
    return getBasicProduitFields();
  }
};

// Version statique des champs (sans options chargées)
export const produitCreationFields = [
  ...getBasicProduitFields(),
  { 
    name: 'categoryId', 
    label: 'Catégorie', 
    type: 'select', 
    options: [],
    placeholder: 'Sélectionner une catégorie',
    col: 6
  },
  { 
    name: 'supplierId', 
    label: 'Fournisseur principal', 
    type: 'select', 
    options: [],
    placeholder: 'Sélectionner un fournisseur',
    col: 6
  }
];

// Fonction pour formater les données produit
export const formatProduitData = (formData) => {
  return {
    name: formData.name?.trim(),
    description: formData.description?.trim(),
    type: formData.type,
    unit: formData.unit,
    categoryId: formData.categoryId || undefined,
    supplierId: formData.supplierId || undefined,
    costPrice: parseFloat(formData.costPrice) || 0,
    sellingPrice: parseFloat(formData.sellingPrice) || 0,
    supplierReference: formData.supplierReference?.trim(),
    active: formData.active !== false,
    inventory: {
      minStock: parseInt(formData['inventory.minStock']) || 5,
      maxStock: parseInt(formData['inventory.maxStock']) || 100,
      currentStock: parseInt(formData['inventory.currentStock']) || 0
    },
    dimensions: {
      width: parseFloat(formData['dimensions.width']) || 0,
      height: parseFloat(formData['dimensions.height']) || 0,
      depth: parseFloat(formData['dimensions.depth']) || 0,
      unit: 'cm'
    },
    weight: {
      value: parseFloat(formData['weight.value']) || 0,
      unit: 'kg'
    }
  };
};

// Fonction pour pré-remplir les données produit
export const prepareProduitFormData = (produit) => {
  if (!produit) return {};
  
  return {
    name: produit.name || '',
    description: produit.description || '',
    type:'consommable' ,
    unit: produit.unit || 'Piece',
    categoryId: produit.categoryId?._id || produit.categoryId || '',
    supplierId: produit.supplierId?._id || produit.supplierId || '',
    costPrice: produit.costPrice || 0,
    sellingPrice: produit.sellingPrice || 0,
    supplierReference: produit.supplierReference || '',
    active: produit.active !== false,
    'inventory.minStock': produit.inventory?.minStock || 5,
    'inventory.maxStock': produit.inventory?.maxStock || 100,
    'inventory.currentStock': produit.inventory?.currentStock || 0,
    'dimensions.width': produit.dimensions?.width || 0,
    'dimensions.height': produit.dimensions?.height || 0,
    'dimensions.depth': produit.dimensions?.depth || 0,
    'weight.value': produit.weight?.value || 0
  };
};

// Fonction de validation
export const validateProduitForm = (formData) => {
  const errors = {};
  
  if (!formData.name?.trim()) {
    errors.name = 'Le nom du produit est obligatoire';
  }

  
  if (!formData.unit) {
    errors.unit = 'L\'unité de mesure est obligatoire';
  }
  
  const minStock = parseInt(formData['inventory.minStock']) || 0;
  const maxStock = parseInt(formData['inventory.maxStock']) || 0;
  if (minStock > maxStock) {
    errors['inventory.minStock'] = 'Le stock minimum ne peut pas être supérieur au stock maximum';
  }
  
  const costPrice = parseFloat(formData.costPrice) || 0;
  const sellingPrice = parseFloat(formData.sellingPrice) || 0;
  if (sellingPrice > 0 && costPrice > sellingPrice) {
    errors.costPrice = 'Le prix d\'achat ne peut pas être supérieur au prix de vente';
  }
  
  return errors;
};

// Hook personnalisé pour les champs produit
export const useProduitCreationFields = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadFields = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const loadedFields = await getProduitCreationFields(forceRefresh);
      setFields(loadedFields);
    } catch (err) {
      setError(err.message);
      console.error('Erreur chargement champs produit:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fonction pour forcer le rechargement
  const refreshFields = useCallback(() => {
    return loadFields(true);
  }, [loadFields]);

  return {
    fields,
    loading,
    error,
    loadFields,
    refreshFields
  };
};

// Export par défaut
export default {
  creation: produitCreationFields,
  getDynamicCreationFields: getProduitCreationFields,
  useProduitCreationFields,
  update: [...produitCreationFields],
  format: formatProduitData,
  prepare: prepareProduitFormData,
  validate: validateProduitForm,
  loadCategories,
  loadSuppliers
};